import qrcode from "qrcode-generator";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { imageUrl } from "../../services/assets/assets.ts";
import { formatNumber, LANGUAGE_NAMES, LANGUAGES, setLanguage, useLanguage } from "../../i18n/lang.ts";
import { vi } from "../../i18n/vi.ts";

export function Art({ id, alt = "", className, size, tint }: { id: string; alt?: string; className?: string; size?: number; tint?: string }) {
  const src = imageUrl(id);
  if (!src) return <span className={`art art--missing ${className ?? ""}`} style={size ? { width: size, height: size } : undefined} />;
  const style = size ? { width: size, height: size } : undefined;
  if (tint) {
    // Multiply-style recolour for pearl-white tintable art (same idea as Phaser setTint).
    return (
      <span className={`art art--tinted ${className ?? ""}`} style={{ ...style, ["--tint" as string]: tint, ["--src" as string]: `url(${src})` }} role="img" aria-label={alt}>
        <img src={src} alt="" draggable={false} />
      </span>
    );
  }
  return <img className={`art ${className ?? ""}`} src={src} alt={alt} style={style} draggable={false} />;
}

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  icon,
  className,
  testId,
}: {
  children?: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost" | "gold" | "danger";
  disabled?: boolean;
  icon?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={`btn btn--${variant} ${className ?? ""}`}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      {icon ? <Art id={icon} className="btn__icon" /> : null}
      {children ? <span className="btn__label">{children}</span> : null}
    </button>
  );
}

export function IconButton({ icon, label, onClick, disabled, testId }: { icon: string; label: string; onClick: () => void; disabled?: boolean; testId?: string }) {
  return (
    <button type="button" className="icon-btn" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}>
      <Art id={icon} />
    </button>
  );
}

export function Sheet({
  title,
  onClose,
  children,
  className,
  testId,
}: {
  title?: ReactNode;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  useEffect(() => {
    if (!onClose) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sheet-backdrop" onPointerDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <section className={`sheet ${className ?? ""}`} role="dialog" aria-modal="true" data-testid={testId}>
        {(title || onClose) && (
          <header className="sheet__header">
            <h2 className="sheet__title">{title}</h2>
            {onClose ? <IconButton icon="icon.close" label={vi.close} onClick={onClose} testId="sheet-close" /> : null}
          </header>
        )}
        <div className="sheet__body">{children}</div>
      </section>
    </div>
  );
}

export function Coins({ amount, pending }: { amount: number; pending?: number }) {
  return (
    <span className="coins" data-testid="coins">
      <Art id="reward.coin" className="coins__icon" />
      <span className="coins__value">{formatNumber(amount)}</span>
      {pending ? (
        <span className="coins__pending">
          <Art id="icon.coin-wings" className="ico-inline" />
          {pending} {vi.hud.pending}
        </span>
      ) : null}
    </span>
  );
}

export function Stars({ count, size = 22, animate }: { count: number; size?: number; animate?: boolean }) {
  return (
    <span className={`stars ${animate ? "stars--animate" : ""}`} aria-label={vi.starsLabel(count)}>
      {[1, 2, 3].map((n) => (
        <Art key={n} id={n <= count ? "reward.star" : "reward.star-empty"} size={size} className={`stars__star stars__star--${n}`} />
      ))}
    </span>
  );
}

export function Toggle({ label, checked, onChange, disabled, note }: { label: string; checked: boolean; onChange: (value: boolean) => void; disabled?: boolean; note?: string }) {
  return (
    <label className={`toggle ${disabled ? "toggle--disabled" : ""}`}>
      <span className="toggle__label">
        {label}
        {note ? <small className="toggle__note">{note}</small> : null}
      </span>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className="toggle__track" aria-hidden="true">
        <span className="toggle__thumb" />
      </span>
    </label>
  );
}

/** Two pills, each language written in itself; switching re-renders the whole app at once. */
export function LanguageSwitch({ className }: { className?: string }) {
  const current = useLanguage();
  return (
    <div className={`lang-switch ${className ?? ""}`} role="radiogroup" aria-label={vi.settings.language} data-testid="language-switch">
      {LANGUAGES.map((lang) => (
        <button
          key={lang}
          type="button"
          role="radio"
          aria-checked={current === lang}
          lang={lang}
          className={`lang-switch__option ${current === lang ? "lang-switch__option--active" : ""}`}
          onClick={() => setLanguage(lang)}
          data-testid={`language-${lang}`}
        >
          {LANGUAGE_NAMES[lang]}
        </button>
      ))}
    </div>
  );
}

/** QR code drawn locally as SVG; the text never leaves the device. */
export function QrCode({ text, className }: { text: string; className?: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 5, margin: 2, scalable: true });
  }, [text]);
  return <div className={`qr ${className ?? ""}`} dangerouslySetInnerHTML={{ __html: svg }} />;
}
